import React, { useState, useRef, useEffect } from 'react';
import { Clock, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { inputBaseClass } from './input-styles';
import { Button } from './button';

interface TimePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
}

const QUICK_MINUTES = [0, 15, 30, 45];

interface SpinnerColumnProps {
  value: number;
  label: string;
  onIncrement: () => void;
  onDecrement: () => void;
}

const SpinnerColumn: React.FC<SpinnerColumnProps> = ({ value, label, onIncrement, onDecrement }) => (
  <div className="flex flex-col items-center gap-1">
    <Button variant="ghost" size="icon-sm" onClick={onIncrement} aria-label={`Increase ${label}`}>
      <ChevronUp />
    </Button>
    <div className="numeric grid h-12 w-14 place-items-center rounded-lg border border-line-strong bg-canvas/60 text-xl font-medium text-fg">
      {value.toString().padStart(2, '0')}
    </div>
    <Button variant="ghost" size="icon-sm" onClick={onDecrement} aria-label={`Decrease ${label}`}>
      <ChevronDown />
    </Button>
  </div>
);

export const TimePicker: React.FC<TimePickerProps> = ({
  id,
  value,
  onChange,
  className,
  placeholder = "HH:MM",
  disabled = false,
  invalid = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Parse initial value
  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':').map(Number);
      setHours(h || 0);
      setMinutes(m || 0);
      setInputValue(value);
    } else {
      setInputValue('');
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatTime = (h: number, m: number) => {
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const handleTimeChange = (newHours: number, newMinutes: number) => {
    setHours(newHours);
    setMinutes(newMinutes);
    onChange(formatTime(newHours, newMinutes));
  };

  const incrementHours = () => handleTimeChange((hours + 1) % 24, minutes);
  const decrementHours = () => handleTimeChange(hours === 0 ? 23 : hours - 1, minutes);
  const incrementMinutes = () => handleTimeChange(hours, (minutes + 1) % 60);
  const decrementMinutes = () => handleTimeChange(hours, minutes === 0 ? 59 : minutes - 1);

  const clearTime = () => {
    onChange('');
    setInputValue('');
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and a single colon, max HH:MM
    const sanitizedInput = e.target.value.replace(/[^0-9:]/g, '');
    if ((sanitizedInput.match(/:/g) || []).length > 1 || sanitizedInput.length > 5) {
      return;
    }

    setInputValue(sanitizedInput);

    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    if (timeRegex.test(sanitizedInput)) {
      const [h, m] = sanitizedInput.split(':').map(Number);
      setHours(h);
      setMinutes(m);
      onChange(sanitizedInput);
    }
  };

  const handleInputBlur = () => {
    // If input is invalid, revert to current value
    if (value && !inputValue) {
      setInputValue(value);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Enter', 'Escape', 'Tab', 'Home', 'End'
    ];

    if (e.key === ':' && inputValue.includes(':')) {
      e.preventDefault();
      return;
    }

    if (!allowedKeys.includes(e.key) && e.key !== ':' && !/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      // Keep Escape from also closing the surrounding dialog
      if (isOpen) e.nativeEvent.stopImmediatePropagation();
      setInputValue(value || '');
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <input
          id={id}
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={invalid || undefined}
          className={cn(
            inputBaseClass,
            "numeric pr-10",
            invalid && "border-danger/60 focus:border-danger focus:ring-danger/20"
          )}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          aria-label="Open time picker"
          aria-expanded={isOpen}
          className="absolute top-1/2 right-1.5 grid size-7 -translate-y-1/2 place-items-center rounded-md text-fg-subtle transition-colors hover:bg-white/[0.06] hover:text-fg disabled:opacity-50"
        >
          <Clock size={15} />
        </button>
      </div>

      {isOpen && (
        <div className="absolute top-full right-0 left-0 z-50 mt-1.5 min-w-52 animate-pop-in rounded-xl border border-line-strong bg-elevated p-3 shadow-pop">
          <div className="flex items-center justify-center gap-2">
            <SpinnerColumn value={hours} label="hours" onIncrement={incrementHours} onDecrement={decrementHours} />
            <span className="pb-0.5 text-xl font-medium text-fg-subtle">:</span>
            <SpinnerColumn value={minutes} label="minutes" onIncrement={incrementMinutes} onDecrement={decrementMinutes} />
          </div>

          <div className="mt-3 grid grid-cols-4 gap-1">
            {QUICK_MINUTES.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => handleTimeChange(hours, m)}
                className={cn(
                  "numeric h-7 rounded-md text-xs transition-colors",
                  minutes === m && value
                    ? "bg-brand/20 text-[#d4c2ff]"
                    : "text-fg-muted hover:bg-white/[0.06] hover:text-fg"
                )}
              >
                :{m.toString().padStart(2, '0')}
              </button>
            ))}
          </div>

          {value && (
            <div className="mt-2 border-t border-line pt-2">
              <Button variant="ghost" size="sm" className="w-full" onClick={clearTime}>
                Clear time
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
